import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import PlatformHubTabs from "../components/PlatformHubTabs";
import { api } from "../lib/api";
import OnlyFansLiveStats from "../components/OnlyFansLiveStats";
import OperationsPulse from "../components/OperationsPulse";
import { useAutoRefresh, triggerGlobalRefresh } from "../lib/autoRefresh";
import {
  Crown, TrendingUp, DollarSign, Video, RefreshCw, ExternalLink,
  Users, Eye, Heart, Settings, Save, Trash2, ToggleLeft, ToggleRight,
  Flame, MessageSquare, Award, FileText, Landmark
} from "lucide-react";
import { toast } from "sonner";

const OF_VIP_PURPLE = "#a855f7";

export default function OnlyFansVIPHub() {
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [useManualData, setUseManualData] = useState(true);
  const [manualStats, setManualStats] = useState({
    subscribers: 0,
    posts_count: 0,
    total_revenue: 0,
    subscription_revenue: 0,
    tips: 0,
    messages_revenue: 0,
    ppv_revenue: 0,
    streams_revenue: 0
  });
  
  // Dashboard state with auto-refresh
  const { data: stats } = useAutoRefresh(
    "/api/onlyfans/stats/vip",
    null,
    ['onlyfans_vip', '*'],
    { fetchOnMount: true, debounce: 200, silent: true }
  );
  
  const [extensionStats, setExtensionStats] = useState(null);

  useEffect(() => {
    loadSettings();
    loadExtensionData();
  }, []);
  
  const loadExtensionData = async () => {
    try {
      const res = await api.get("/api/platform-data/stats/onlyfans_vip");
      setExtensionStats(res.data);
    } catch (e) {
      console.error("Failed to load extension stats:", e);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await api.get("/api/onlyfans/settings/vip");
      if (res.data) {
        setUseManualData(res.data.use_manual_data ?? true);
        setManualStats(res.data.manual_stats || manualStats);
      }
    } catch (e) {
      console.error("Failed to load settings:", e);
    }
  };

  const saveManualStats = async () => {
    try {
      await api.post("/api/onlyfans/manual-stats/vip", { stats: manualStats });
      toast.success("Manual stats saved!");
      triggerGlobalRefresh('onlyfans_vip');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save stats");
    }
  };

  const toggleDataSource = async () => {
    const newValue = !useManualData;
    setUseManualData(newValue);
    try {
      await api.post("/api/onlyfans/settings/vip", {
        use_manual_data: newValue
      });
      toast.success(newValue ? "Using manual data" : "Using live sync data");
      triggerGlobalRefresh('onlyfans_vip');
    } catch (e) {
      toast.error("Failed to update settings");
    }
  };

  const deleteManualData = async () => {
    if (!confirm("Delete all manual data? This will use live sync only.")) return;
    try {
      await api.delete("/api/onlyfans/manual-stats/vip");
      setManualStats({
        subscribers: 0,
        posts_count: 0,
        total_revenue: 0,
        subscription_revenue: 0,
        tips: 0,
        messages_revenue: 0,
        ppv_revenue: 0,
        streams_revenue: 0
      });
      setUseManualData(false);
      toast.success("Manual data deleted. Using live sync.");
      triggerGlobalRefresh('onlyfans_vip');
    } catch (e) {
      toast.error("Failed to delete manual data");
    }
  };

  const manualRefresh = async () => {
    setLoading(true);
    try {
      const r = await api.post("/api/onlyfans/sync/vip");
      if (r.data.ok) {
        toast.success("OnlyFans VIP data synced successfully!");
      } else {
        toast.error(r.data.error || "Sync failed");
      }
      triggerGlobalRefresh('onlyfans_vip');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setLoading(false);
    }
  };

  const displayStats = useManualData 
    ? (stats && Object.keys(stats).length > 0 ? stats : manualStats)
    : (extensionStats || manualStats);

  return (
    <AppLayout
      title="OnlyFans VIP Hub"
      overline="Platform CRM · Premium Content · Performance"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSettingsOpen(!settingsOpen)}
            className="btn-ghost inline-flex items-center gap-2 backdrop-blur-xl bg-black/40 border border-white/20 text-white hover:border-purple-500">
            <Settings size={16} />
            Settings
          </button>
          <button
            onClick={manualRefresh}
            disabled={loading}
            className="btn-ghost inline-flex items-center gap-2 backdrop-blur-xl bg-black/40 border border-purple-500/50 text-purple-300 hover:border-purple-500">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            {loading ? "Syncing..." : "Sync Now"}
          </button>
          <a
            href="https://onlyfans.com/giopilosvip"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost inline-flex items-center gap-2 text-xs backdrop-blur-xl bg-black/40 border border-white/20 text-white hover:border-purple-500">
            <ExternalLink size={14} />
            Open OnlyFans VIP
          </a>
        </div>
      }>
      <PlatformHubTabs platform="onlyfans_vip" platformName="OnlyFans VIP">
      <OperationsPulse
        title="VIP operating pulse"
        subtitle="Premium monetization, audience state, and content performance at a glance."
        accent={OF_VIP_PURPLE}
        metrics={[
          { label: "Subscribers", value: displayStats?.subscribers || 0, color: "#c084fc", detail: "VIP audience" },
          { label: "Revenue", value: `$${Number(displayStats?.total_revenue || 0).toFixed(2)}`, color: "#22c55e", detail: "tracked total" },
          { label: "PPV + messages", value: `$${(Number(displayStats?.ppv_revenue || 0) + Number(displayStats?.messages_revenue || 0)).toFixed(2)}`, color: "#f59e0b", detail: "monetization" },
          { label: "Premium posts", value: displayStats?.posts_count || 0, color: "#00AFF0", detail: "content volume" },
        ]}
        gap="⚠️ Limited: Statement data, payouts, retention metrics, and conversions pending backend expansion."
      />
      
      {/* Settings Panel */}
      {settingsOpen && (
        <div className="mb-6 backdrop-blur-xl bg-purple-900/20 border border-white/10 rounded-lg p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Settings size={20} className="text-purple-400" />
            OnlyFans VIP Settings
          </h3>

          {/* Data Source Toggle */}
          <div className="mb-6 p-4 rounded-lg bg-black/40 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-semibold text-white">Data Source</div>
                <div className="text-sm text-zinc-400">
                  {useManualData ? "Using manual entry" : "Using live sync from OnlyFans"}
                </div>
              </div>
              <button
                onClick={toggleDataSource}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition backdrop-blur-xl bg-purple-600 hover:bg-purple-700 text-white">
                {useManualData ? <ToggleLeft size={20} /> : <ToggleRight size={20} />}
                {useManualData ? "Switch to Live" : "Switch to Manual"}
              </button>
            </div>
          </div>

          {/* Manual Stats Entry */}
          {useManualData && (
            <div className="p-4 rounded-lg bg-black/40 border border-white/10">
              <h4 className="font-semibold text-white mb-4">Manual Stats Entry</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Subscribers</label>
                  <input
                    type="number"
                    value={manualStats.subscribers}
                    onChange={(e) => setManualStats({...manualStats, subscribers: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Posts</label>
                  <input
                    type="number"
                    value={manualStats.posts_count}
                    onChange={(e) => setManualStats({...manualStats, posts_count: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Total Revenue ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualStats.total_revenue}
                    onChange={(e) => setManualStats({...manualStats, total_revenue: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Subscription Revenue ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualStats.subscription_revenue}
                    onChange={(e) => setManualStats({...manualStats, subscription_revenue: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Tips ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualStats.tips}
                    onChange={(e) => setManualStats({...manualStats, tips: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Messages Revenue ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualStats.messages_revenue}
                    onChange={(e) => setManualStats({...manualStats, messages_revenue: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">PPV Revenue ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualStats.ppv_revenue}
                    onChange={(e) => setManualStats({...manualStats, ppv_revenue: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Streams Revenue ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualStats.streams_revenue}
                    onChange={(e) => setManualStats({...manualStats, streams_revenue: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 rounded bg-black/60 border border-white/10 text-white"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={saveManualStats}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium backdrop-blur-xl bg-green-600 hover:bg-green-700 text-white">
                  <Save size={16} />
                  Save Manual Stats
                </button>
                <button
                  onClick={deleteManualData}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium backdrop-blur-xl bg-red-600/80 hover:bg-red-700 text-white">
                  <Trash2 size={16} />
                  Delete Manual Data
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          icon={Crown}
          label="VIP Subscribers"
          value={displayStats?.subscribers || 0}
          color="#a855f7"
        />
        <KPICard
          icon={Video}
          label="Premium Posts"
          value={displayStats?.posts_count || 0}
          color="#00AFF0"
        />
        <KPICard
          icon={DollarSign}
          label="Total Revenue"
          value={`$${(displayStats?.total_revenue || 0).toFixed(2)}`}
          color="#22c55e"
        />
        <KPICard
          icon={TrendingUp}
          label="Subscriptions"
          value={`$${(displayStats?.subscription_revenue || 0).toFixed(2)}`}
          color="#14b8a6"
        />
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <KPICard
          icon={Heart}
          label="Tips"
          value={`$${(displayStats?.tips || 0).toFixed(2)}`}
          color="#ec4899"
        />
        <KPICard
          icon={MessageSquare}
          label="Messages Revenue"
          value={`$${(displayStats?.messages_revenue || 0).toFixed(2)}`}
          color="#3b82f6"
        />
        <KPICard
          icon={Award}
          label="PPV Revenue"
          value={`$${(displayStats?.ppv_revenue || 0).toFixed(2)}`}
          color="#f59e0b"
        />
        <KPICard
          icon={Flame}
          label="Streams Revenue"
          value={`$${(displayStats?.streams_revenue || 0).toFixed(2)}`}
          color="#f97316"
        />
      </div>

      {/* Live Stats (best-effort browser-cookie scrape) */}
      <OnlyFansLiveStats account="vip" accent="#a855f7" />

      {/* Management overview */}
      <div className="backdrop-blur-xl bg-purple-900/20 border border-white/10 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Crown size={24} className="text-purple-500 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-2">OnlyFans VIP Platform Management</h3>
            <p className="text-sm text-zinc-400 mb-4">
              Manage your premium OnlyFans VIP business with full visibility into subscriber performance, revenue, PPV sales, statements, payouts, and exclusive-content metrics. This hub is the main control center for your premium monetization workflow.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-4">
                <div className="font-semibold text-purple-400 mb-1">VIP Exclusivity</div>
                <div className="text-zinc-400">Premium subscriber-only content, direct access, and high-value interactions.</div>
              </div>
              <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-4">
                <div className="font-semibold text-green-400 mb-1">Revenue Tracking</div>
                <div className="text-zinc-400">Subscriptions, tips, PPV, message unlocks, and streams are shown above when available.</div>
              </div>
              <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-4">
                <div className="font-semibold text-blue-400 mb-1">Engagement Metrics</div>
                <div className="text-zinc-400">Subscriber performance, content volume, 1-on-1 interactions, and personalized content signals.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reporting coverage */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <FileText size={20} className="text-green-400" />
            <h3 className="font-semibold text-white">Earnings statements</h3>
          </div>
          <p className="text-sm text-zinc-400">The hub exposes only the earnings fields safely provided by its current contract. Statement rows and historical statement imports are downstream Finance data, not hub-native reporting.</p>
          <a href="/crm/money?tab=imports" className="inline-flex items-center mt-3 text-sm text-green-300 hover:text-green-200">Open Finance imports</a>
        </div>
        <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <Landmark size={20} className="text-amber-400" />
            <h3 className="font-semibold text-white">Payout activity</h3>
          </div>
          <p className="text-sm text-zinc-400">Account-level payout rows are outside the current VIP hub API contract. Imported payout records remain downstream Finance data; full hub-native payouts require backend/data-model expansion.</p>
          <a href="/crm/money?tab=revenue" className="inline-flex items-center mt-3 text-sm text-amber-300 hover:text-amber-200">Open Finance payouts</a>
        </div>
      </div>
      </PlatformHubTabs>
    </AppLayout>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  return (
    <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-lg p-6 hover:border-purple-500/50 transition">
      <div className="flex items-center gap-3 mb-2">
        <Icon size={24} style={{ color }} />
        <div className="text-sm font-medium text-zinc-400">{label}</div>
      </div>
      <div className="text-3xl font-bold text-white">{value}</div>
    </div>
  );
}
